import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const relatedArticles = article ? getRelatedArticles(article.id, article.category) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!article) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center bg-background-950">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-800">
            <i className="ri-error-warning-line text-foreground-500 text-3xl"></i>
          </div>
          <h2 className="font-heading text-2xl text-foreground-200 mb-2">文章未找到</h2>
          <p className="text-foreground-500 mb-6">该文章可能已被移除或链接无效</p>
          <Link
            to="/"
            className="inline-flex px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 font-medium rounded-lg transition-all duration-200 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const pageSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.publishDate,
      image: article.imageUrl,
      url: `https://wwyjs.cn/article/${article.id}`,
      articleSection: article.categoryName,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `${article.title} 属于哪个分类？`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `本文属于夜无疆「${article.categoryName}」分类，该分类下的内容涵盖${article.categoryName}相关的深度探索与分享。`,
          },
        },
        {
          '@type': 'Question',
          name: '夜无疆是什么平台？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '夜无疆是一个关于夜晚无限可能的探索平台，涵盖夜间生活、创作灵感、深夜情绪和夜行者社群等多元内容。',
          },
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        schema={pageSchema}
      />
      <main className="pt-20 bg-background-950">
        {/* Article Header */}
        <section className="py-12 md:py-16 bg-background-900">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <div className="mb-6">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex px-3 py-1.5 rounded-full bg-primary-500/15 text-primary-400 text-sm font-medium hover:bg-primary-500/25 transition-colors duration-200"
              >
                {article.categoryName}
              </Link>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-100 mb-6 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <div className="flex items-center gap-2">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-9 h-9 rounded-full object-cover"
                  loading="lazy"
                />
                <span className="text-foreground-300">{article.author}</span>
              </div>
              <span className="text-foreground-700">|</span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-800 text-foreground-500 text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-8 relative z-10">
          <div className="rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-auto object-cover"
            />
          </div>
        </div>

        {/* Article Content */}
        <article className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div
              className="prose prose-invert prose-lg max-w-none
                prose-p:text-foreground-300 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
                prose-strong:text-foreground-100 prose-strong:font-semibold
                prose-h2:font-heading prose-h2:text-2xl prose-h2:text-foreground-100 prose-h2:mt-10 prose-h2:mb-5
                prose-h3:font-heading prose-h3:text-xl prose-h3:text-foreground-200 prose-h3:mt-8 prose-h3:mb-4
                prose-li:text-foreground-400 prose-li:text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {/* Author Bio */}
        <section className="py-10 bg-background-900 border-t border-b border-background-800/50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="flex items-start gap-4">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                loading="lazy"
              />
              <div>
                <h4 className="font-heading text-foreground-200 text-lg mb-1">{article.author}</h4>
                <p className="text-foreground-500 text-sm leading-relaxed">
                  夜无疆专栏作者，热爱夜晚的探索者与记录者。致力于用文字捕捉夜晚的每一个动人瞬间。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 bg-background-950">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h3 className="font-heading text-2xl text-foreground-200 mb-8">相关文章</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group rounded-xl bg-background-900 border border-background-800/50 hover:border-primary-500/20 transition-all duration-300 overflow-hidden cursor-pointer"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <div className="text-xs text-foreground-600 mb-2">{rel.publishDate}</div>
                      <h4 className="font-heading text-foreground-200 group-hover:text-primary-400 transition-colors duration-200 mb-2 line-clamp-2">
                        {rel.title}
                      </h4>
                      <p className="text-foreground-500 text-sm line-clamp-2">{rel.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}